import { useNavigate } from 'react-router';
import { CheckCircle2 } from 'lucide-react';
import type { Phase } from '@airmail/engine';
import { Button } from '@/ui';
import { useT } from '@/i18n';
import { phaseLabel } from '@/layout/AppShell';
import { usePhaseAbschluss } from '@/lib/phaseAbschluss';

/**
 * Stand einer laufenden Phase mit dem Weg zum Abschluss — dort, wo man am
 * Turniertag ist (Spiele, Start), nicht nur auf „Auslosung".
 *
 * Solange gespielt wird: eine Zeile „Gruppenphase · 18 von 24 gewertet · 2
 * laufen · 4 offen", dahinter für die Leitung ein kleiner Knopf „Phase
 * abschließen …" für den Fall, dass am Abend noch Spiele übrig bleiben.
 * Sind alle Spiele gewertet, wird daraus die Aufforderung: „Alle Spiele
 * gewertet" mit dem großen Knopf „Phase abschließen" bzw. „Turnier beenden"
 * (letzte Phase). Danach geht es zur Seite der nächsten Phase oder zur
 * Tabelle mit dem Endstand.
 *
 * Ein Helfer sieht nur den Stand — abschließen darf er nicht (Server).
 * `nurFertig`: nur die Aufforderung zeigen (Startseite am Handy).
 */
export function PhaseAbschlussLeiste({ phase, nurFertig }: { phase: Phase; nurFertig?: boolean }) {
  const t = useT();
  const navigate = useNavigate();
  const a = usePhaseAbschluss(phase);
  const name = t(phaseLabel(phase.type));
  const knopf = a.letztePhase ? t('Turnier beenden') : t('Phase abschließen');
  const los = async () => {
    if (await a.abschliessen()) void navigate(a.ziel);
  };

  if (a.fertig && a.moeglich) {
    return (
      <div role="status" data-phase-fertig={phase.id} className="bg-panel2 border border-red px-4 py-3 mb-3 flex flex-wrap items-center gap-3">
        <CheckCircle2 size={18} className="text-live shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-bold tracking-tight">{t('Alle Spiele gewertet')}</div>
          <div className="font-mono text-[11px] text-dim">
            {name} · {t('{n} von {m} Spielen gewertet', { n: a.gewertet, m: a.gesamt })}
          </div>
        </div>
        {a.darf && (
          <div className="flex flex-wrap gap-2 max-md:w-full">
            {/* KO-Runden: nach einer fertigen Runde ist die nächste Runde die eigentliche Wahl — abschließen erst, wenn die Leitung entscheidet, dass es reicht */}
            {a.weiter && (
              <Button variant="primary" className="max-md:flex-1" onClick={() => void navigate(a.weiter as string)}>
                {t('Nächste Runde starten')}
              </Button>
            )}
            <Button variant={a.weiter ? 'default' : 'primary'} className="max-md:flex-1" onClick={los} data-phase-abschliessen>
              <CheckCircle2 size={14} /> {knopf}
            </Button>
          </div>
        )}
      </div>
    );
  }
  if (nurFertig) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 font-mono text-[11.5px] text-dim" data-phase-stand={phase.id}>
      <span>{t('{phase} · {n} von {m} gewertet · {r} laufen · {o} offen', { phase: name, n: a.gewertet, m: a.gesamt, r: a.laufend, o: a.offen })}</span>
      {a.darf && a.erzwingbar && a.gewertet > 0 && (
        <Button size="sm" variant="ghost" className="ml-auto" onClick={los} title={t('Offene Spiele werden abgesagt — mit Rückfrage')}>
          {knopf} …
        </Button>
      )}
    </div>
  );
}
